import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import useSeo from "../hooks/useSeo";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import WhatsAppFloat from "../components/landing/WhatsAppFloat";
import { getPostBySlug, POSTS } from "../data/posts";
import { SITE, DAFTAR_URL } from "../config/links";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPostBySlug(slug);

    const canonical = post ? `${SITE.domain}/blog/${post.slug}` : SITE.domain;
    const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

    useSeo({
        title: post
            ? `${post.title} | PUSATCUAN`
            : "Artikel PUSATCUAN",
        description: post?.excerpt,
        keywords: post?.keywords,
        canonical,
        image: post?.cover,
        type: "article",
        jsonLd: post && {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "BlogPosting",
                    headline: post.title,
                    description: post.excerpt,
                    datePublished: post.date,
                    dateModified: post.date,
                    image: post.cover,
                    mainEntityOfPage: canonical,
                    author: { "@type": "Organization", name: "PUSATCUAN" },
                    publisher: {
                        "@type": "Organization",
                        name: "PUSATCUAN",
                        logo: {
                            "@type": "ImageObject",
                            url: `${SITE.domain}/logo.png`,
                        },
                    },
                },
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        {
                            "@type": "ListItem",
                            position: 1,
                            name: "Beranda",
                            item: `${SITE.domain}/`,
                        },
                        {
                            "@type": "ListItem",
                            position: 2,
                            name: "Blog",
                            item: `${SITE.domain}/blog`,
                        },
                        {
                            "@type": "ListItem",
                            position: 3,
                            name: post.title,
                            item: canonical,
                        },
                    ],
                },
            ],
        },
    });

    if (!post) return <Navigate to="/blog" replace />;

    return (
        <main data-testid="blog-post-page" className="bg-[#050505] text-white">
            <Header />

            <article className="relative pt-32 pb-20 md:pt-44">
                <div className="absolute inset-0 h-[420px] overflow-hidden">
                    <img
                        src={post.cover}
                        alt={post.title}
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/70 to-[#050505]" />
                </div>

                <div className="relative max-w-3xl mx-auto px-6 md:px-12">
                    <Link
                        to="/blog"
                        data-testid="back-to-blog"
                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#D4AF37] hover:text-[#F3E5AB]"
                    >
                        <ArrowLeft size={14} /> Kembali ke Jurnal
                    </Link>
                    <span className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D4AF37]/30 text-[11px] tracking-[0.2em] uppercase text-[#D4AF37]">
                        {post.category}
                    </span>
                    <h1 className="mt-5 text-3xl md:text-5xl font-medium tracking-tighter leading-[1.1]">
                        {post.title}
                    </h1>
                    <div className="mt-6 flex items-center gap-5 text-xs text-white/50">
                        <span className="inline-flex items-center gap-1.5">
                            <Calendar size={12} />
                            {new Date(post.date).toLocaleDateString("id-ID", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Clock size={12} /> {post.read}
                        </span>
                    </div>

                    <div className="mt-12 space-y-6 text-white/75 text-base md:text-lg leading-relaxed">
                        {post.sections.map((s, i) =>
                            s.type === "h2" ? (
                                <h2
                                    key={i}
                                    className="text-2xl md:text-3xl font-medium text-white tracking-tight pt-6"
                                >
                                    {s.text}
                                </h2>
                            ) : (
                                <p key={i}>{s.text}</p>
                            ),
                        )}
                    </div>

                    <div className="mt-14 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#111] to-[#0a0a0a] p-8 md:p-10 text-center">
                        <h3 className="text-2xl md:text-3xl font-medium tracking-tight">
                            Siap Coba Sendiri di{" "}
                            <span className="text-gold-gradient">PUSATCUAN</span>?
                        </h3>
                        <p className="mt-3 text-white/60">
                            Daftarkan akun Anda lewat link resmi terpercaya dalam 1 menit.
                        </p>
                        <a
                            data-testid="post-cta-daftar"
                            href={DAFTAR_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F3E5AB] text-black font-semibold px-7 py-3.5 rounded-full shadow-gold-glow hover:scale-105 transition-all"
                        >
                            Daftar Sekarang <ArrowRight size={16} />
                        </a>
                    </div>
                </div>
            </article>

            {others.length > 0 && (
                <section className="py-20 border-t border-white/5 bg-[#080808]">
                    <div className="max-w-5xl mx-auto px-6 md:px-12">
                        <h3 className="text-2xl md:text-3xl font-medium tracking-tight mb-10">
                            Artikel lainnya
                        </h3>
                        <div className="grid md:grid-cols-2 gap-8">
                            {others.map((p, i) => (
                                <Link
                                    key={p.slug}
                                    to={`/blog/${p.slug}`}
                                    data-testid={`related-post-${i}`}
                                    className="group rounded-2xl border border-white/5 bg-[#0c0c0c] overflow-hidden hover:border-[#D4AF37]/40 transition-all"
                                >
                                    <div className="aspect-[16/9] overflow-hidden">
                                        <img
                                            src={p.cover}
                                            alt={p.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">
                                            {p.category}
                                        </span>
                                        <h4 className="mt-3 text-lg font-medium group-hover:text-[#D4AF37]">
                                            {p.title}
                                        </h4>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <Footer />
            <WhatsAppFloat />
        </main>
    );
}
